import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DataTable, type Column } from '../../components/table/DataTable';
import FormattedDateRangeInput from '../../components/ui/FormattedDateRangeInput';
import MultiSelectChips from '../../components/ui/MultiSelectChips';
import {
  lkStatusPillClass,
  type LkPublicationStatus,
} from '../../data/legalKnowledge';
import { getFileCategories, getSubFileCategories } from '../../services/criminalCaseRequests';
import {
  fetchExtractedCaseOutcomeList,
  type ExtractedCaseOutcomeListRow,
  type ExtractedCaseOutcomeListRequestBody,
} from '../../services/legalKnowledgeArchive';
import { LK_PERMISSIONS as LK } from '../../rbac/legalKnowledgePermissions';
import { usePermission } from '../../rbac/hooks';
import { formatDocumentTableDateTime } from '../../utils/dateFormat';

type FilterState = {
  archiveRefNo: string;
  fileRefNo: string;
  fileCategories: string[];
  subFileCategories: string[];
  statuses: string[];
  text: string;
  caseCompletedFrom: string;
  caseCompletedTo: string;
  outcomeExtractedFrom: string;
  outcomeExtractedTo: string;
};

const INITIAL_FILTER_STATE: FilterState = {
  archiveRefNo: '',
  fileRefNo: '',
  fileCategories: [],
  subFileCategories: [],
  statuses: [],
  text: '',
  caseCompletedFrom: '',
  caseCompletedTo: '',
  outcomeExtractedFrom: '',
  outcomeExtractedTo: '',
};

const SORT_KEY_MAP: Record<string, string> = {
  archiveReferenceNumber: 'REFERENCE_NUMBER',
  fileReferenceNumber: 'FILE_REFERENCE_NUMBER',
  fileCategoryName: 'FILE_CATEGORY',
  subFileCategoryName: 'SUB_FILE_CATEGORY',
  statusCode: 'STATUS',
  caseCompletedAt: 'CASE_COMPLETED_AT',
  outcomeExtractedAt: 'OUTCOME_EXTRACTED_AT',
};

const toIsoOrNull = (dateStr: string): string | null => {
  if (!dateStr) return null;
  return new Date(dateStr + 'T00:00:00').toISOString();
};

const cellOrDash = (value: string | null | undefined) => value?.trim() || '-';

export default function ExtractedCaseOutcomesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { has } = usePermission();
  const canViewOutcomeDetails = has(LK.VIEW_EXTRACTED_CASE_OUTCOMES);
  const canViewArchivedCase = has(LK.VIEW_ARCHIVE_CASES);
  const [pending, setPending] = useState<FilterState>(INITIAL_FILTER_STATE);
  const [applied, setApplied] = useState<FilterState>(INITIAL_FILTER_STATE);
  const [rows, setRows] = useState<ExtractedCaseOutcomeListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [uiPage, setUiPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [sortKey, setSortKey] = useState<string>('fileReferenceNumber');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [fileCategoryOptions, setFileCategoryOptions] = useState<{ id: string; label: string }[]>([]);
  const [fileCategoryLookup, setFileCategoryLookup] = useState<Map<string, string>>(new Map());
  const [subCategoryOptions, setSubCategoryOptions] = useState<{ id: string; label: string }[]>([]);
  const [searchTrigger, setSearchTrigger] = useState(0);
  const today = useMemo(() => new Date().toISOString().split('T')[0], []);

  useEffect(() => {
    let active = true;
    const body: ExtractedCaseOutcomeListRequestBody = {
      reference_number: applied.archiveRefNo.trim() || null,
      file_reference_number: applied.fileRefNo.trim() || null,
      file_category_names: applied.fileCategories.length > 0 ? applied.fileCategories : null,
      sub_file_category_names: applied.subFileCategories.length > 0 ? applied.subFileCategories : null,
      status_codes: applied.statuses.length > 0 ? applied.statuses.map((s) => s.toUpperCase()) : null,
      search_text: applied.text.trim() || null,
      case_completed_at_from: toIsoOrNull(applied.caseCompletedFrom),
      case_completed_at_to: toIsoOrNull(applied.caseCompletedTo),
      outcome_extracted_at_from: toIsoOrNull(applied.outcomeExtractedFrom),
      outcome_extracted_at_to: toIsoOrNull(applied.outcomeExtractedTo),
      sort_by: SORT_KEY_MAP[sortKey] ?? 'FILE_REFERENCE_NUMBER',
      sort_direction: sortDir.toUpperCase() as 'ASC' | 'DESC',
    };
    fetchExtractedCaseOutcomeList(body, Math.max(0, uiPage - 1), pageSize)
      .then((result) => {
        if (!active) return;
        setRows(result.content);
        setTotalCount(result.total_elements);
        setTotalPages(result.total_pages);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (!active) return;
        console.error('Failed to load extracted case outcomes', err);
        setRows([]);
        setTotalCount(0);
        setTotalPages(0);
        setLoading(false);
      });
    return () => { active = false; };
  }, [uiPage, pageSize, sortKey, sortDir, applied, searchTrigger]);

  useEffect(() => {
    getFileCategories().then((result) => {
      if (!result.success) return;
      setFileCategoryOptions(result.data.map((c) => ({ id: c.name, label: c.name })));
      setFileCategoryLookup(new Map(result.data.map((c) => [c.name, c.id])));
    });
  }, []);

  useEffect(() => {
    let active = true;
    if (pending.fileCategories.length === 0) {
      Promise.resolve().then(() => { if (active) setSubCategoryOptions([]); });
      return () => { active = false; };
    }
    const ids = pending.fileCategories
      .map((name) => fileCategoryLookup.get(name))
      .filter((id): id is string => Boolean(id));
    if (ids.length === 0) return () => { active = false; };
    Promise.all(ids.map((id) => getSubFileCategories(id))).then((results) => {
      if (!active) return;
      const seen = new Set<string>();
      const merged: { id: string; label: string }[] = [];
      for (const result of results) {
        if (!result.success) continue;
        for (const item of result.data) {
          if (!seen.has(item.name)) {
            seen.add(item.name);
            merged.push({ id: item.name, label: item.name });
          }
        }
      }
      setSubCategoryOptions(merged);
    });
    return () => { active = false; };
  }, [pending.fileCategories, fileCategoryLookup]);

  const lkStatusT = useCallback(
    (s: LkPublicationStatus) => t(`legalKnowledge.status.${s}`),
    [t],
  );

  const statusFilterOptions = useMemo(
    () => [
      { id: 'draft', label: t('legalKnowledge.status.draft') },
      { id: 'published', label: t('legalKnowledge.status.published') },
      { id: 'rejected', label: t('legalKnowledge.status.rejected') },
    ],
    [t],
  );

  const columns = useMemo<Column<ExtractedCaseOutcomeListRow>[]>(
    () => [
      {
        key: 'archiveReferenceNumber',
        header: t('legalKnowledge.outcomes.colArchiveRefNo'),
        sortable: true,
        getValue: (r) => cellOrDash(r.archive_reference_number),
        render: (r) => <span className="rn">{cellOrDash(r.archive_reference_number)}</span>,
      },
      {
        key: 'fileReferenceNumber',
        header: t('legalKnowledge.outcomes.colFileRefNo'),
        sortable: true,
        getValue: (r) => cellOrDash(r.file_reference_number),
        render: (r) => (
          <span style={{ color: r.file_reference_number ? undefined : 'var(--t3)' }}>
            {cellOrDash(r.file_reference_number)}
          </span>
        ),
      },
      {
        key: 'fileCategoryName',
        header: t('legalKnowledge.outcomes.colFileCategory'),
        sortable: true,
        getValue: (r) => cellOrDash(r.file_category_name),
        render: (r) => (
          <span style={{ color: r.file_category_name ? undefined : 'var(--t3)' }}>
            {cellOrDash(r.file_category_name)}
          </span>
        ),
      },
      {
        key: 'subFileCategoryName',
        header: t('legalKnowledge.outcomes.colSubFileCategory'),
        sortable: true,
        getValue: (r) => cellOrDash(r.sub_file_category_name),
        render: (r) => (
          <span style={{ color: r.sub_file_category_name ? undefined : 'var(--t3)' }}>
            {cellOrDash(r.sub_file_category_name)}
          </span>
        ),
      },
      {
        key: 'outcomeExtractedAt',
        header: t('legalKnowledge.outcomes.colOutcomeExtractedAt'),
        sortable: true,
        getValue: (r) => r.outcome_extracted_at ?? '',
        render: (r) => (
          <span style={{ fontSize: 12 }}>
            {r.outcome_extracted_at ? formatDocumentTableDateTime(r.outcome_extracted_at) : '-'}
          </span>
        ),
      },
      {
        key: 'caseCompletedAt',
        header: t('legalKnowledge.outcomes.colCaseCompletedAt'),
        sortable: true,
        getValue: (r) => r.case_completed_at ?? '',
        render: (r) => (
          <span style={{ fontSize: 12 }}>
            {r.case_completed_at ? formatDocumentTableDateTime(r.case_completed_at) : '-'}
          </span>
        ),
      },
      {
        key: 'statusCode',
        header: t('common.status'),
        sortable: true,
        getValue: (r) => r.status_code,
        render: (r) => (
          <span className={lkStatusPillClass(r.status_code.toLowerCase() as LkPublicationStatus)}>
            {lkStatusT(r.status_code.toLowerCase() as LkPublicationStatus)}
          </span>
        ),
      },
      {
        key: 'actions',
        header: t('common.actions'),
        sortable: false,
        searchable: false,
        getValue: () => '',
        render: (r) => (
          <div className="act-wrap">
            {r.status_code === 'DRAFT' && canViewOutcomeDetails ? (
              <button
                type="button"
                className="act-b"
                title={t('legalKnowledge.outcomes.editDraftTip')}
                onClick={() =>
                  navigate(`/legal-knowledge/archive/management/outcomes/${r.extracted_case_outcome_id}?caseRequestId=${r.case_request_id}&status=${r.status_code}`)
                }
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
            ) : r.status_code === 'REJECTED' && canViewOutcomeDetails ? (
              <button
                type="button"
                className="act-b"
                title={t('legalKnowledge.outcomes.viewTip')}
                onClick={() =>
                  navigate(`/legal-knowledge/archive/management/outcomes/${r.extracted_case_outcome_id}?caseRequestId=${r.case_request_id}&status=${r.status_code}`)
                }
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </button>
            ) : r.status_code !== 'DRAFT' && r.status_code !== 'REJECTED' && canViewArchivedCase ? (
              <button
                type="button"
                className="act-b"
                title={t('legalKnowledge.outcomes.viewArchivedTip')}
                onClick={() => navigate(`/legal-knowledge/archive/${r.extracted_case_outcome_id}`)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </button>
            ) : null}
          </div>
        ),
      },
    ],
    [canViewArchivedCase, canViewOutcomeDetails, navigate, t, lkStatusT],
  );

  const handleReset = () => {
    setPending(INITIAL_FILTER_STATE);
    setLoading(true);
    setApplied(INITIAL_FILTER_STATE);
    setUiPage(1);
    setSearchTrigger((n) => n + 1);
  };

  return (
    <>
      <div className="bc">
        <span>{t('nav.groups.legalKnowledgeArchiveManagement')}</span>
        <span>/</span>
        <span>{t('nav.extractedCaseOutcomes')}</span>
      </div>

      <div className="page-hdr">
        <div>
          <div className="pg-title">{t('legalKnowledge.outcomes.title')}</div>
        </div>
      </div>

      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--rs)',
          padding: '20px 24px',
          marginBottom: 20,
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          <div className="ccr-fg">
            <label>{t('legalKnowledge.outcomes.archiveReferenceNumber')}</label>
            <input
              className="ccr-inp"
              type="text"
              maxLength={255}
              placeholder={t('legalKnowledge.outcomes.archiveReferenceNumberPlaceholder')}
              value={pending.archiveRefNo}
              onChange={(e) => setPending((p) => ({ ...p, archiveRefNo: e.target.value }))}
            />
          </div>
          <div className="ccr-fg">
            <label>{t('legalKnowledge.outcomes.fileReferenceNumber')}</label>
            <input
              className="ccr-inp"
              type="text"
              maxLength={255}
              placeholder={t('legalKnowledge.outcomes.fileReferenceNumberPlaceholder')}
              value={pending.fileRefNo}
              onChange={(e) => setPending((p) => ({ ...p, fileRefNo: e.target.value }))}
            />
          </div>
          <MultiSelectChips
            label={t('legalKnowledge.outcomes.fileCategories')}
            value={pending.fileCategories}
            options={fileCategoryOptions}
            placeholder={t('legalKnowledge.outcomes.fileCategoriesPlaceholder')}
            onChange={(vals) => setPending((p) => ({ ...p, fileCategories: vals, subFileCategories: [] }))}
          />
          <MultiSelectChips
            label={t('legalKnowledge.outcomes.subFileCategories')}
            value={pending.subFileCategories}
            options={subCategoryOptions}
            placeholder={t('legalKnowledge.outcomes.subFileCategoriesPlaceholder')}
            onChange={(vals) => setPending((p) => ({ ...p, subFileCategories: vals }))}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginTop: 16 }}>
          <MultiSelectChips
            label={t('legalKnowledge.outcomes.statuses')}
            value={pending.statuses}
            options={statusFilterOptions}
            placeholder={t('legalKnowledge.outcomes.statusesPlaceholder')}
            onChange={(vals) => setPending((p) => ({ ...p, statuses: vals }))}
          />
          <div className="ccr-fg">
            <label>{t('legalKnowledge.outcomes.enterText')}</label>
            <input
              className="ccr-inp"
              type="text"
              maxLength={255}
              placeholder={t('legalKnowledge.outcomes.enterTextPlaceholder')}
              value={pending.text}
              onChange={(e) => setPending((p) => ({ ...p, text: e.target.value }))}
            />
          </div>
          <div className="ccr-fg">
            <label>{t('legalKnowledge.outcomes.caseCompletedFrom')}</label>
            <FormattedDateRangeInput
              from={pending.caseCompletedFrom}
              to={pending.caseCompletedTo}
              maxDate={today}
              onChange={(f, t) => setPending((p) => ({ ...p, caseCompletedFrom: f, caseCompletedTo: t }))}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
          <div style={{ flex: '0 0 calc(25% - 12px)' }}>
            <div className="ccr-fg">
              <label>{t('legalKnowledge.outcomes.outcomeExtractedFrom')}</label>
              <FormattedDateRangeInput
                from={pending.outcomeExtractedFrom}
                to={pending.outcomeExtractedTo}
                maxDate={today}
                onChange={(f, t) => setPending((p) => ({ ...p, outcomeExtractedFrom: f, outcomeExtractedTo: t }))}
              />
            </div>
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ flex: '0 0 auto', alignSelf: 'flex-end', display: 'flex', gap: 10 }}>
            <button type="button" className="mfb" onClick={handleReset}>
              {t('common.reset')}
            </button>
            <button
              type="button"
              className="mfb mfb-dk"
              onClick={() => {
                setLoading(true);
                setApplied(pending);
                setUiPage(1);
                setSearchTrigger((n) => n + 1);
              }}
            >
              {t('common.search')}
            </button>
          </div>
        </div>
      </div>

      <DataTable
        title={t('legalKnowledge.outcomes.tableTitle')}
        loading={loading}
        enableHeaderSearch={false}
        emphasizedHeader
        columns={columns}
        rows={rows}
        rowKey={(r) => r.extracted_case_outcome_id}
        defaultRowsPerPage={10}
        rowsPerPageOptions={[5, 10, 15, 20]}
        footerCountNoun={t('legalKnowledge.outcomes.footerCountNoun')}
        previousLabel={t('common.previous')}
        nextLabel={t('common.next')}
        serverPaging={{
          totalCount,
          totalPages,
          page: uiPage,
          pageSize,
          onPageChange: (p) => {
            setLoading(true);
            setUiPage(p);
          },
          onPageSizeChange: (s) => {
            setLoading(true);
            setPageSize(s);
            setUiPage(1);
          },
        }}
        serverSort={{
          key: sortKey,
          dir: sortDir,
          onSortChange: (key, dir) => {
            setLoading(true);
            setSortKey(key);
            setSortDir(dir);
            setUiPage(1);
          },
        }}
      />
    </>
  );
}
